import { BellAlertIcon } from '@heroicons/react/24/outline';
import { DmMessageAttachment } from './DmMessageAttachment';
import { PendingAttachmentPreview, PendingUploadStatus } from './composer/PendingAttachmentPreview';
import { isPending, type DisplayMessage, type PendingMessage } from './composer/pendingMessages';

interface DmMessageBubbleProps {
    message: DisplayMessage;
    onRetry: (message: PendingMessage) => void;
    retryDisabled: boolean;
}

const RETRY_WAIT_HINT = 'Wait for the current message to send';

function formatMessageTime(isoString: string): string {
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function bubbleClassFor(message: DisplayMessage): string {
    if (message.sender === 'patient') return 'bg-gray-100 text-gray-700 rounded-bl-sm';
    if (message.sender === 'system') return 'bg-blue-50 text-blue-800 rounded-br-sm';
    const sending = isPending(message) && message.status === 'sending';
    return `bg-[#007E87] text-white rounded-br-sm ${sending ? 'opacity-60' : ''}`;
}

function BubbleAttachment({ message }: { message: DisplayMessage }) {
    if (isPending(message) && message.outgoing.attachment) {
        return (
            <PendingAttachmentPreview
                attachment={message.outgoing.attachment}
                senderName={message.senderName}
            />
        );
    }
    if (!message.attachment) return null;
    return <DmMessageAttachment attachment={message.attachment} senderName={message.senderName} />;
}

interface PendingFooterProps {
    message: PendingMessage;
    onRetry: () => void;
    retryDisabled: boolean;
}

function PendingFooter({ message, onRetry, retryDisabled }: PendingFooterProps) {
    if (message.status === 'failed') {
        return (
            <div role="alert" className="flex flex-col items-end gap-0.5 max-w-[75%] text-right">
                {message.errorMessage && (
                    <span className="text-xs text-red-600 break-words">{message.errorMessage}</span>
                )}
                <button
                    type="button"
                    onClick={onRetry}
                    disabled={retryDisabled}
                    title={retryDisabled ? RETRY_WAIT_HINT : undefined}
                    className="text-red-600 text-xs underline hover:text-red-800 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:text-red-600"
                >
                    Not sent. Retry
                </button>
            </div>
        );
    }
    return null;
}

function DmMessageBubble({ message, onRetry, retryDisabled }: DmMessageBubbleProps) {
    const isPatient = message.sender === 'patient';
    const pending = isPending(message) ? message : null;
    const hasAttachment = pending ? pending.outgoing.attachment !== null : !!message.attachment;
    const hasText = message.text.trim() !== '';
    const sending = pending?.status === 'sending' ? pending : null;
    const sendingAttachment = sending?.outgoing.attachment ?? null;

    return (
        <div className={`flex flex-col gap-1 ${isPatient ? 'items-start' : 'items-end'}`}>
            <div
                className={`max-w-[75%] px-3.5 py-2.5 rounded-2xl text-sm leading-snug whitespace-pre-wrap ${bubbleClassFor(message)}`}
            >
                {hasAttachment && (
                    <div className={hasText || sending ? 'mb-2' : undefined}>
                        <BubbleAttachment message={message} />
                    </div>
                )}
                {message.sender === 'system' && hasText && (
                    <BellAlertIcon className="w-3.5 h-3.5 text-blue-500 inline-block align-text-bottom mr-1.5" />
                )}
                {hasText && message.text}
                {sending && sendingAttachment && (
                    <div className={hasText ? 'mt-1.5' : undefined}>
                        <PendingUploadStatus
                            attachment={sendingAttachment}
                            uploadPercent={sending.uploadPercent}
                        />
                    </div>
                )}
                {sending && !sendingAttachment && (
                    <span className="ml-2 inline-block w-3 h-3 rounded-full border-2 border-white border-t-transparent animate-spin align-middle" />
                )}
            </div>
            {pending && (
                <PendingFooter
                    message={pending}
                    onRetry={() => onRetry(pending)}
                    retryDisabled={retryDisabled}
                />
            )}
            <span className="text-[10px] text-gray-400">
                {message.senderName} · {formatMessageTime(message.createdAt)}
            </span>
        </div>
    );
}

export { DmMessageBubble };
